/**
 * The AI customer report: what the model is told, what it may say back, and
 * how an answer is checked before anybody sees it.
 *
 * The model is an explainer, never a source of figures. So:
 *
 *  - **It is given keyed facts** (`buildReportInput`), computed by the same
 *    rules as the Book, and a few enums that say what the account's state *is*.
 *    Text a person or a spreadsheet wrote — the customer's name, notes,
 *    history — travels separately, marked as untrusted data.
 *  - **It may not write a figure or a date.** Every amount, date and count in
 *    its answer is a placeholder, `{{fact_key}}`, which the screen fills from
 *    the facts the report was written from. A number the model made up has
 *    nowhere to appear: a stray digit fails validation (`validateAiReport`).
 *  - **It must restate the account's state** (in credit or owing, follow-up
 *    state, promise state) in fields that are checked against the facts. A
 *    report calling a credit balance a receivable is rejected, not shown.
 *  - **It may not score, predict, approve or refuse.** No credit score, no
 *    probability, no limit recommendation: those words fail validation.
 *
 * Nothing here does I/O. The server builds the input and validates the output;
 * the screen renders placeholders with `splitPlaceholders`.
 */

/** Bumped whenever the instructions change. Recorded on every run. */
export const AI_REPORT_PROMPT_VERSION = 'customer-report/2';
/** Bumped whenever the answer's shape changes. */
export const AI_REPORT_SCHEMA_VERSION = 'customer-report-schema/1';

import type { AgeingWindows } from './customer.js';
import { ageingWindowRows } from './receivablesBook.js';

/* ------------------------------ the facts in ----------------------------- */

/** What `ai_customer_facts()` returns: the account as the caller may see it. */
export interface AiCustomerFacts {
    today: string;
    currency: string;
    customer: {
        name: string;
        category: string | null;
        city: string | null;
        payment_terms_days: number | null;
        credit_limit: number | null;
        is_urgent: boolean;
        rank: 'Good' | 'Late' | 'Bad';
        rank_set_by_person: boolean;
        assigned: boolean;
        notes: string;
        updated_at: string;
    };
    balance: {
        amount: number;
        type: 'Dr' | 'Cr' | string | null;
        status: string;
        bucket_1_45: number; bucket_46_90: number; bucket_91_135: number; bucket_over_135: number;
        /** A TallyPrime account's own windows (docs/tally/18 §1.3): the bands its company reads money in, named as Settings names them. */
        windows?: AgeingWindows | null;
        over_45: number; over_90: number; over_135: number;
        settled_on: string | null;
    };
    follow_up: {
        state: FollowUpState;
        date: string | null;
        last_contacted: string | null;
        expected_amount: number | null;
        expected_on: string | null;
    };
    latest_promise: {
        amount: number | null;
        on: string | null;
        made_on: string;
        answered_by: string | null;
    } | null;
    activity: {
        on: string;
        kind: string;
        text: string;
        promised_amount: number | null;
        promised_on: string | null;
    }[];
    activity_total: number;
    activity_90_days: Record<'notes' | 'no_answer' | 'declined' | 'promises' | 'payments_logged' | 'visits' | 'disputes', number>;
    cheques_visible: boolean;
    cheques: { state: ChequeFactState; amount: number; date: string | null }[];
    cheque_totals: {
        in_hand_count: number; in_hand_amount: number; on_hold_count: number;
        bounced_count: number; bounced_amount: number;
        cleared_count: number; cleared_amount: number;
    } | null;
    version: {
        customer_updated_at: string;
        latest_activity_at: string | null;
        cheques_updated_at: string | null;
    };
}

export type Position = 'owes_us' | 'in_credit' | 'settled';
export type FollowUpState = 'overdue' | 'today' | 'future' | 'no_follow_up' | 'completed';
export type ReportPromiseState = 'none' | 'upcoming' | 'due_today' | 'date_passed' | 'answered';
export type ChequeFactState = 'due' | 'overdue' | 'upcoming' | 'hold' | 'bounced' | 'cleared';

export type FactKind = 'money' | 'date' | 'count' | 'days' | 'text';
export interface Fact {
    label: string;
    kind: FactKind;
    value: number | string;
}
export type FactMap = Record<string, Fact>;

/** What the answer must say about the account's state — checked, not trusted. */
export interface ExpectedClaims {
    position: Position;
    follow_up_state: FollowUpState;
    promise_state: ReportPromiseState;
}

/** What the model receives. Deliberately small; see 08 §12.3. */
export interface AiReportInput {
    report_date: string;
    currency: string;
    position: Position;
    follow_up_state: FollowUpState;
    promise_state: ReportPromiseState;
    rank: 'Good' | 'Late' | 'Bad';
    rank_set_by_person: boolean;
    is_urgent: boolean;
    assigned: boolean;
    cheques_visible: boolean;
    facts: FactMap;
    /** The names of the ageing bands this account is read in: the only digits the answer may write itself. */
    ageing_band_names: string[];
    cheques: { state: ChequeFactState; amount_fact: string; date_fact: string | null }[];
    recent_activity: { on_fact: string; kind: string; text: string; promised_amount_fact: string | null; promised_on_fact: string | null }[];
    /** Written by people or imported from a spreadsheet. Data, never instructions. */
    untrusted: {
        customer_name: string;
        category: string | null;
        city: string | null;
        account_notes: string;
    };
    /** What this data cannot tell anybody, stated so the report does not guess. */
    known_limits: string[];
}

const daysBetween = (from: string, to: string): number =>
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export function positionOf(facts: AiCustomerFacts): Position {
    if (!facts.balance.amount) return 'settled';
    return facts.balance.type === 'Cr' ? 'in_credit' : 'owes_us';
}

export function promiseStateOf(facts: AiCustomerFacts): ReportPromiseState {
    const p = facts.latest_promise;
    if (!p) return 'none';
    if (p.answered_by) return 'answered';
    if (!p.on) return 'upcoming';
    if (p.on === facts.today) return 'due_today';
    return p.on < facts.today ? 'date_passed' : 'upcoming';
}

/** Bounded, and nothing that is a person's contact detail or an internal id. */
export const AI_INPUT_LIMITS = {
    activity: 10,
    activityText: 300,
    notes: 500,
    cheques: 10,
    name: 200,
    shortText: 80,
} as const;

const clip = (value: string | null | undefined, max: number): string =>
    (value ?? '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').slice(0, max);

/**
 * The model's input, the facts its placeholders may name, and the claims its
 * answer must make. Every figure goes into `facts` under a stable key.
 */
export function buildReportInput(facts: AiCustomerFacts): {
    input: AiReportInput; factMap: FactMap; expected: ExpectedClaims;
} {
    const factMap: FactMap = {};
    const put = (key: string, label: string, kind: FactKind, value: number | string | null | undefined) => {
        if (value === null || value === undefined || value === '') return null;
        if (kind !== 'text' && kind !== 'date' && !Number.isFinite(Number(value))) return null;
        factMap[key] = { label, kind, value: kind === 'text' || kind === 'date' ? String(value) : Number(value) };
        return key;
    };

    const position = positionOf(facts);
    const expected: ExpectedClaims = {
        position,
        follow_up_state: facts.follow_up.state,
        promise_state: promiseStateOf(facts),
    };

    put('report_date', 'Date of this report', 'date', facts.today);
    put('customer_name', 'The customer', 'text', clip(facts.customer.name, AI_INPUT_LIMITS.name));
    put('outstanding', position === 'in_credit' ? 'Credit balance held for the customer' : 'Amount owed to the company',
        'money', facts.balance.amount);
    // The bands the account is read in: a TallyPrime company's own windows (D3) — with what is not yet due and the credit
    // held against no bill — or a sheet's four columns.
    const windows = facts.balance.windows ?? null;
    let bandNames: string[];
    if (windows) {
        const rows = ageingWindowRows(windows);
        rows.forEach((r, i) => put(`window_${i + 1}`, r.label, 'money', r.amount));
        if (windows.notYetDue > 0) put('not_yet_due', 'Not yet due', 'money', windows.notYetDue);
        if (windows.credits > 0) put('credit_against_no_bill', 'Credit held for the customer against no bill', 'money', windows.credits);
        bandNames = rows.map(r => r.label);
    } else {
        put('bucket_1_45', 'Overdue 1–45 days', 'money', facts.balance.bucket_1_45);
        put('bucket_46_90', 'Overdue 46–90 days', 'money', facts.balance.bucket_46_90);
        put('bucket_91_135', 'Overdue 91–135 days', 'money', facts.balance.bucket_91_135);
        put('bucket_over_135', 'Overdue over 135 days', 'money', facts.balance.bucket_over_135);
        bandNames = [...STANDARD_BAND_NAMES];
    }
    put('overdue_over_45', 'Overdue more than 45 days', 'money', facts.balance.over_45);
    put('overdue_over_90', 'Overdue more than 90 days', 'money', facts.balance.over_90);
    put('overdue_over_135', 'Overdue more than 135 days', 'money', facts.balance.over_135);
    put('settled_on', 'Date the balance was first seen cleared', 'date', facts.balance.settled_on);
    put('credit_limit', 'Credit limit on record', 'money', facts.customer.credit_limit);
    put('payment_terms_days', 'Payment terms on record', 'days', facts.customer.payment_terms_days);

    if (facts.follow_up.date) {
        put('follow_up_date', 'Next follow-up date', 'date', facts.follow_up.date);
        const delta = daysBetween(facts.today, facts.follow_up.date);
        if (delta < 0) put('follow_up_days_overdue', 'Days the follow-up is overdue', 'days', -delta);
        if (delta > 0) put('follow_up_days_ahead', 'Days until the follow-up', 'days', delta);
    }
    if (facts.follow_up.last_contacted) {
        put('last_contacted', 'Last follow-up recorded', 'date', facts.follow_up.last_contacted);
        put('days_since_contact', 'Days since the last follow-up', 'days',
            Math.max(0, daysBetween(facts.follow_up.last_contacted, facts.today)));
    }
    put('expected_amount', 'Amount the team expects (a promise or forecast, not a payment)', 'money', facts.follow_up.expected_amount);
    put('expected_on', 'Date the team expects it', 'date', facts.follow_up.expected_on);

    if (facts.latest_promise) {
        put('promise_amount', 'Amount in the latest promise (not a payment)', 'money', facts.latest_promise.amount);
        put('promise_on', 'Date promised', 'date', facts.latest_promise.on);
        put('promise_made_on', 'Date the promise was recorded', 'date', facts.latest_promise.made_on);
        if (facts.latest_promise.on && facts.latest_promise.on < facts.today && !facts.latest_promise.answered_by) {
            put('promise_days_passed', 'Days since the promised date', 'days', daysBetween(facts.latest_promise.on, facts.today));
        }
    }

    put('activity_total', 'Entries in the account history', 'count', facts.activity_total);
    const counts = facts.activity_90_days;
    put('calls_unanswered_90d', 'Unanswered calls in the last 90 days', 'count', counts.no_answer);
    put('declined_90d', 'Calls where they declined, last 90 days', 'count', counts.declined);
    put('promises_90d', 'Promises recorded in the last 90 days', 'count', counts.promises);
    put('payments_logged_90d', 'Payments logged by the team in the last 90 days (not a ledger)', 'count', counts.payments_logged);
    put('visits_90d', 'Visits in the last 90 days', 'count', counts.visits);
    put('disputes_90d', 'Disputes recorded in the last 90 days', 'count', counts.disputes);
    put('notes_90d', 'Notes and answered calls in the last 90 days', 'count', counts.notes);

    if (facts.cheques_visible && facts.cheque_totals) {
        const t = facts.cheque_totals;
        put('cheques_in_hand_count', 'Cheques in hand, not yet cleared', 'count', t.in_hand_count);
        put('cheques_in_hand_amount', 'Value of cheques in hand (not yet money)', 'money', t.in_hand_amount);
        put('cheques_on_hold_count', 'Cheques on hold', 'count', t.on_hold_count);
        put('cheques_bounced_count', 'Cheques that bounced', 'count', t.bounced_count);
        put('cheques_bounced_amount', 'Value of cheques that bounced', 'money', t.bounced_amount);
        put('cheques_cleared_count', 'Cheques recorded as cleared', 'count', t.cleared_count);
        put('cheques_cleared_amount', 'Value of cheques recorded as cleared', 'money', t.cleared_amount);
    }

    const cheques = facts.cheques_visible
        ? facts.cheques.slice(0, AI_INPUT_LIMITS.cheques).map((c, i) => ({
            state: c.state,
            amount_fact: put(`cheque_${i + 1}_amount`, `Cheque ${i + 1} amount`, 'money', c.amount) ?? `cheque_${i + 1}_amount`,
            date_fact: put(`cheque_${i + 1}_date`, `Cheque ${i + 1} date`, 'date', c.date),
        }))
        : [];

    const recent_activity = facts.activity.slice(0, AI_INPUT_LIMITS.activity).map((a, i) => ({
        on_fact: put(`activity_${i + 1}_on`, `Date of history entry ${i + 1}`, 'date', a.on) ?? `activity_${i + 1}_on`,
        kind: a.kind,
        text: clip(a.text, AI_INPUT_LIMITS.activityText),
        promised_amount_fact: put(`activity_${i + 1}_promised_amount`, `Amount promised in entry ${i + 1}`, 'money', a.promised_amount),
        promised_on_fact: put(`activity_${i + 1}_promised_on`, `Date promised in entry ${i + 1}`, 'date', a.promised_on),
    }));

    const known_limits = [
        'The balance and ageing come from the most recent sync of the company\'s receivables data (its sheet, or TallyPrime); nothing here says when individual invoices were paid.',
        'Timely Payment does not hold a history of payments received. A promise or an expected amount is not a payment. A payment entry is a person recording that they were told they were paid.',
        'A cheque in hand is not money until it clears. Cleared cheques are only those recorded in Timely Payment.',
        'Ageing is as of today only; there is no record of past ageing.',
    ];
    if (!facts.cheques_visible) known_limits.push('Cheque information is not visible to the person asking, so it is not included. Do not say there are no cheques.');
    // Against what is sent, not what arrived: the builder caps it too.
    if (facts.activity_total > recent_activity.length) {
        known_limits.push('Only the most recent history entries are included; older ones exist and are not shown here.');
    }

    return {
        factMap,
        expected,
        input: {
            report_date: facts.today,
            currency: clip(facts.currency, 8),
            position,
            follow_up_state: facts.follow_up.state,
            promise_state: expected.promise_state,
            rank: facts.customer.rank,
            rank_set_by_person: facts.customer.rank_set_by_person,
            is_urgent: facts.customer.is_urgent,
            assigned: facts.customer.assigned,
            cheques_visible: facts.cheques_visible,
            facts: factMap,
            ageing_band_names: bandNames,
            cheques,
            recent_activity,
            untrusted: {
                customer_name: clip(facts.customer.name, AI_INPUT_LIMITS.name),
                category: facts.customer.category ? clip(facts.customer.category, AI_INPUT_LIMITS.shortText) : null,
                city: facts.customer.city ? clip(facts.customer.city, AI_INPUT_LIMITS.shortText) : null,
                account_notes: clip(facts.customer.notes, AI_INPUT_LIMITS.notes),
            },
            known_limits,
        },
    };
}

/* ------------------------------ the answer out ---------------------------- */

export const STEP_KINDS = [
    'call', 'visit', 'confirm_promise', 'chase_cheque', 'review_account',
    'update_follow_up', 'escalate_internally', 'resolve_dispute', 'other',
] as const;
export type StepKind = (typeof STEP_KINDS)[number];

export interface Observation { text: string; facts: string[] }
export interface Step { text: string; kind: StepKind; facts: string[] }

export interface AiReport {
    position: Position;
    follow_up_state: FollowUpState;
    promise_state: ReportPromiseState;
    summary: string;
    current_position: Observation[];
    needs_attention: Observation[];
    positive_signals: Observation[];
    suggested_next_steps: Step[];
    data_limitations: string[];
}

export const AI_REPORT_LIMITS = {
    summary: 600,
    observation: 400,
    step: 300,
    limitation: 300,
    current_position: [1, 4],
    needs_attention: [0, 6],
    positive_signals: [0, 4],
    suggested_next_steps: [0, 5],
    data_limitations: [0, 6],
    factRefs: 8,
    totalBytes: 16_000,
} as const;

/** A sheet's four bands, as the answer may name them. */
const STANDARD_BAND_NAMES: readonly string[] = ['1-45', '46-90', '91-135', 'over 135'];

/**
 * The digits the answer may contain outside placeholders: those in the names
 * of the account's ageing bands, and the 45, 90 and 135 days its "more than"
 * facts are named by (overdue_over_45, _90, _135). For a sheet's account that
 * is the set it always was.
 */
export const allowedNumbersFor = (bandNames: readonly string[] = STANDARD_BAND_NAMES): ReadonlySet<string> =>
    new Set(['45', '90', '135', ...bandNames.flatMap(name => name.match(/\d+/g) ?? [])]);

const PLACEHOLDER = /\{\{\s*([a-z0-9_]{1,48})\s*\}\}/g;

/**
 * Words a report of observations has no business using: a score, a rating, a
 * prediction that someone will default, or a credit decision. The report
 * explains what the facts say; it never judges the customer (D13, 01).
 */
const FORBIDDEN_JUDGEMENT = new RegExp([
    /\bcredit ?scores?\b|\bscore of\b|\bprobabilit|\blikelihood\b|\bchance of\b|\bcreditworth/.source,
    /\b(?:approve|approval|reject|refuse|deny|decline)\w*\s+(?:the\s+|their\s+|its\s+)?(?:credit|order|account)\b/.source,
    /\b(?:increase|reduce|raise|lower|set|cut)\w*\s+(?:the\s+|their\s+|its\s+)?credit limit\b|\brecommended credit limit\b|\bblacklist|\bdefault risk\b|%/.source,
    // A prediction of default, however it is put (hosted readiness review, Session 18).
    /\b(?:likely|unlikely|going|set|expected|bound)\s+to\s+default\b|\bwill\s+(?:\w+\s+)?default\b|\b(?:risk|danger)\s+of\s+(?:a\s+)?default\b|\bdefault(?:s|ed|ing)?\s+on\b/.source,
    // A rating of the customer.
    /\b(?:high|low|medium|moderate|elevated)[- ]risk\b|\brisk (?:score|rating|grade|level|profile)\b/.source,
].join('|'), 'i');

/** Signs an answer is echoing its instructions, or something it should never hold. */
const LEAKAGE = /system (?:prompt|instruction|message)|developer (?:prompt|message)|ignore (?:all |any |the )?(?:previous|prior|above) instructions|\bapi[ _-]?key\b|x-goog|AIza[0-9A-Za-z_-]{8,}|bearer\s+[a-z0-9]|service[_ ]role|untrusted|known_limits|\bfacts\b\s*[:=]/i;

const COMPANY_SUFFIX = /\b([A-Z][\w&'.-]*(?:\s+[A-Z][\w&'.-]*){0,4})\s+(Ltd|Limited|Pvt|Private|LLP|Inc|Corp|Corporation|Co\.|Company|Traders|Trading|Industries|Enterprises|Agencies|Sons|Group|Holdings|Coatings|Chemicals)(?!\w)/g;

/** Words that start a sentence, not a business. */
const NOT_A_NAME = new Set(['the', 'this', 'that', 'their', 'our', 'your', 'its', 'his', 'her', 'any',
    'each', 'every', 'another', 'same', 'customer', 'account']);

const tokens = (text: string): Set<string> =>
    new Set(text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(t => t.length > 2 && !NOT_A_NAME.has(t)));

export type ValidationResult =
    | { ok: true; report: AiReport }
    | { ok: false; problems: string[] };

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        && Object.getPrototypeOf(value) === Object.prototype;
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[], where: string, problems: string[]) {
    for (const key of Object.keys(value)) if (!keys.includes(key)) problems.push(`${where}: unexpected field`);
    for (const key of keys) if (!(key in value)) problems.push(`${where}: missing field ${key}`);
}

/**
 * Every rule a piece of the answer's text must pass. The problem strings are
 * ours — never a quotation of the model — so they are safe to log and to send
 * back to the model in a repair request.
 */
export function textProblems(text: unknown, max: number, where: string, factMap: FactMap, customerName: string, allowed: ReadonlySet<string> = allowedNumbersFor()): string[] {
    if (typeof text !== 'string') return [`${where}: not text`];
    const problems: string[] = [];
    const trimmed = text.trim();
    if (!trimmed) problems.push(`${where}: empty`);
    if (text.length > max) problems.push(`${where}: longer than ${max} characters`);
    if (/[<>]/.test(text)) problems.push(`${where}: contains markup`);
    if (/https?:|www\.|:\/\//i.test(text)) problems.push(`${where}: contains an address`);
    if (/[\u0000-\u001f\u007f]/.test(text)) problems.push(`${where}: contains control characters`);
    if (/[`*_#]{2,}|\[[^\]]*\]\(/.test(text)) problems.push(`${where}: contains formatting`);
    if (FORBIDDEN_JUDGEMENT.test(text)) problems.push(`${where}: scores, predicts or decides on credit`);
    if (LEAKAGE.test(text)) problems.push(`${where}: talks about its instructions or credentials`);

    // Placeholders must name facts that exist.
    for (const match of text.matchAll(PLACEHOLDER)) {
        if (!factMap[match[1]!]) problems.push(`${where}: names a fact that does not exist`);
    }
    let rest = text.replace(PLACEHOLDER, ' ');
    if (/[{}]/.test(rest)) problems.push(`${where}: a placeholder is malformed`);

    // The customer's own name, written out, is allowed; a figure is not.
    if (customerName) rest = rest.split(customerName).join(' ');
    // Any decimal digit in any script: a Devanagari figure is still a figure.
    for (const number of rest.match(/\p{Nd}+/gu) ?? []) {
        if (!allowed.has(number)) {
            problems.push(`${where}: writes a figure or date instead of a placeholder`);
            break;
        }
    }

    // A business name the account does not have.
    const own = tokens(customerName);
    for (const match of rest.matchAll(COMPANY_SUFFIX)) {
        const named = tokens(match[1] ?? '');
        if (named.size > 0 && ![...named].some(t => own.has(t))) {
            problems.push(`${where}: names a business other than this customer`);
            break;
        }
    }
    return problems;
}

function factRefProblems(refs: unknown, where: string, factMap: FactMap): string[] {
    if (!Array.isArray(refs)) return [`${where}: facts is not a list`];
    const problems: string[] = [];
    if (refs.length > AI_REPORT_LIMITS.factRefs) problems.push(`${where}: cites too many facts`);
    for (const ref of refs) {
        if (typeof ref !== 'string' || !factMap[ref]) problems.push(`${where}: cites a fact that does not exist`);
    }
    return problems;
}

/**
 * Accept the model's answer, or say exactly which rules it broke.
 *
 * `raw` is untrusted: it is parsed JSON from a provider, and nothing about its
 * shape is assumed.
 */
export function validateAiReport(
    raw: unknown, context: { factMap: FactMap; expected: ExpectedClaims; customerName: string; bandNames?: readonly string[] },
): ValidationResult {
    const { factMap, expected, customerName } = context;
    const allowed = allowedNumbersFor(context.bandNames);
    const problems: string[] = [];

    if (!isPlainObject(raw)) return { ok: false, problems: ['the answer is not an object'] };
    try {
        if (JSON.stringify(raw).length > AI_REPORT_LIMITS.totalBytes) problems.push('the answer is too long');
    } catch {
        return { ok: false, problems: ['the answer cannot be read'] };
    }

    exactKeys(raw, ['position', 'follow_up_state', 'promise_state', 'summary', 'current_position',
        'needs_attention', 'positive_signals', 'suggested_next_steps', 'data_limitations'], 'answer', problems);
    if (problems.length) return { ok: false, problems };

    if (raw['position'] !== expected.position) problems.push('position does not match the account');
    if (raw['follow_up_state'] !== expected.follow_up_state) problems.push('follow_up_state does not match the account');
    if (raw['promise_state'] !== expected.promise_state) problems.push('promise_state does not match the account');

    problems.push(...textProblems(raw['summary'], AI_REPORT_LIMITS.summary, 'summary', factMap, customerName, allowed));

    const list = (name: 'current_position' | 'needs_attention' | 'positive_signals' | 'suggested_next_steps' | 'data_limitations') => {
        const value = raw[name];
        const [min, max] = AI_REPORT_LIMITS[name];
        if (!Array.isArray(value)) {
            problems.push(`${name}: not a list`);
            return [];
        }
        if (value.length < min || value.length > max) problems.push(`${name}: needs between ${min} and ${max} items`);
        return value.slice(0, max);
    };

    for (const name of ['current_position', 'needs_attention', 'positive_signals'] as const) {
        list(name).forEach((item, i) => {
            const where = `${name}[${i}]`;
            if (!isPlainObject(item)) { problems.push(`${where}: not an object`); return; }
            exactKeys(item, ['text', 'facts'], where, problems);
            problems.push(...textProblems(item['text'], AI_REPORT_LIMITS.observation, where, factMap, customerName, allowed));
            problems.push(...factRefProblems(item['facts'], where, factMap));
        });
    }
    list('suggested_next_steps').forEach((item, i) => {
        const where = `suggested_next_steps[${i}]`;
        if (!isPlainObject(item)) { problems.push(`${where}: not an object`); return; }
        exactKeys(item, ['text', 'kind', 'facts'], where, problems);
        if (!STEP_KINDS.includes(item['kind'] as StepKind)) problems.push(`${where}: unknown kind`);
        problems.push(...textProblems(item['text'], AI_REPORT_LIMITS.step, where, factMap, customerName, allowed));
        problems.push(...factRefProblems(item['facts'], where, factMap));
    });
    list('data_limitations').forEach((item, i) => {
        problems.push(...textProblems(item, AI_REPORT_LIMITS.limitation, `data_limitations[${i}]`, factMap, customerName, allowed));
    });

    if (problems.length) return { ok: false, problems: [...new Set(problems)] };
    return { ok: true, report: raw as unknown as AiReport };
}

/* ------------------------------- rendering -------------------------------- */

export type Segment = { text: string } | { fact: string };

/**
 * A validated text, split into plain text and the facts it names, for the
 * screen to render — the facts as the snapshot's own values, formatted by the
 * company's locale and currency. Never HTML.
 */
export function splitPlaceholders(text: string): Segment[] {
    const segments: Segment[] = [];
    let at = 0;
    for (const match of text.matchAll(PLACEHOLDER)) {
        if (match.index! > at) segments.push({ text: text.slice(at, match.index) });
        segments.push({ fact: match[1]! });
        at = match.index! + match[0].length;
    }
    if (at < text.length) segments.push({ text: text.slice(at) });
    return segments;
}

/* ------------------------------- staleness -------------------------------- */

/** What a report was written from, as the screen can compare it. */
export interface ReportBasis {
    customerUpdatedAt: string;
    latestActivityAt: string | null;
    /** Counts and amounts by cheque status, or null when cheques were not visible. */
    cheques: { pending: number; hold: number; bounced: number; cleared: number; amount: number } | null;
}

const sameInstant = (a: string | null | undefined, b: string | null | undefined): boolean => {
    if (!a || !b) return !a && !b;
    return Date.parse(a) === Date.parse(b);
};

export function chequeBasis(cheques: { status: string; amount: number }[]): NonNullable<ReportBasis['cheques']> {
    const basis = { pending: 0, hold: 0, bounced: 0, cleared: 0, amount: 0 };
    for (const c of cheques) {
        if (c.status === 'Pending') basis.pending += 1;
        else if (c.status === 'Hold') basis.hold += 1;
        else if (c.status === 'Bounced') basis.bounced += 1;
        else if (c.status === 'Cleared') basis.cleared += 1;
        basis.amount += Math.abs(Number(c.amount) || 0);
    }
    basis.amount = Math.round(basis.amount * 100) / 100;
    return basis;
}

/** True when the account has changed since the report was written. */
export function reportIsStale(basis: ReportBasis, now: ReportBasis): boolean {
    if (!sameInstant(basis.customerUpdatedAt, now.customerUpdatedAt)) return true;
    if (!sameInstant(basis.latestActivityAt, now.latestActivityAt)) return true;
    if (basis.cheques && now.cheques) {
        const a = basis.cheques;
        const b = now.cheques;
        if (a.pending !== b.pending || a.hold !== b.hold || a.bounced !== b.bounced
            || a.cleared !== b.cleared || Math.abs(a.amount - b.amount) > 0.005) return true;
    }
    return false;
}
